import React, { useCallback, useEffect, useState } from 'react';
import Dropdown from '../Dropdown/Dropdown';
import { ORG_TYPE_TOOLTIP, ZIPCODE_TOOLTIP } from '../../constants/FormConstants';
import { DropdownConstants } from '../../constants/DropdownConstants';
import { DropdownMenu, Grid } from '@cxc/react-fpds-components';
import ZappyButton from '../Button/ZappyButton';
import styles from './InputsFieldsContainer.module.scss';
import { useWindowDimensions } from '@cxc/react-fds-components';
import SearchWithToolTip from '../Search/Search';
import { isDesktop } from '../../utils/utils';
import PlainTextToolTip from '../Tooltips/PlainTextToolTip/PlainTextToolTip';
import { useAtom } from 'jotai';
import { IncentiveApiData } from '../../types/IncentivesDataType';
import { ResidentialIncentiveApiData } from '../../types/ResidentialIncentivesDataType';
import getResidentialIncentives from '../../services/ResidentialIncentiveService';
import {
    equipmentType,
    organizationType,
    status,
    zipCode as zipCodeState,
    filteredIncentivesData,
    incentivesData,
    residentialIncentivesData,
    filteredResidentialIncentivesData,
    incentiveType,
    weightClassData,
    chargerType,
    residentialIncentiveApplicableVehicleType,
    residentialIncentiveElectricityProvider,
    residentialIncentiveGrantorType,
    residentialEquipmentType,
    selectedIncentivesData,
    selectedResidentialIncentiveData,
} from '../../state/GlobalState';
import {
    getUniqueEligibleOrganizations,
    getUniqueCurrentProgramStatuses,
    getUniqueEligibleEquipments,
    getUniqueEligibleChargingTechnology,
} from '../../utils/IncentiveDataHelpers';
import getCommercialIncentives from '../../services/IncentiveService';
import InputFieldsContainerProps from '../../types/props/InputFieldsContainerProps';
import { errorMapping } from '../../constants/ErrorConstants';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import { AxiosError } from 'axios';
import WeightClassDropdown from '../WeightClassDropdown/WeightClassDropdown';
import { isEmpty } from 'lodash';
import { WeightClass } from '../../types/WeightClass';
import { EQUIPMENT_TYPE_CHARGER, EQUIPMENT_TYPE_VEHICLE } from '../../constants/EquipmentConstants';
import {
    EXPORT_PDF_PAGE_RESIDENTIAL_ROUTE,
    EXPORT_PDF_PAGE_COMMERCIAL_ROUTE,
    FPG_PAGE_ROUTE,
} from '../../constants/Routes';
import { useUniversalContext } from '@cxc/react-universal-components';
import { IncentiveTypes } from '../../constants/IncentiveTypes';
import ResidentialInputFieldsContainer from '../ResidentialInputFieldsContainer/ResidentialInputFieldsContainer';
import StatusToolTip from '../Tooltips/StatusToolTip/StatusToolTip';

const InputFieldsContainer = ({ isLoading, setIsLoading }: InputFieldsContainerProps) => {
    const { width } = useWindowDimensions();
    const { navigate } = useUniversalContext();

    const [selectedIncentiveType, setSelectedIncentiveType] = useAtom<string>(incentiveType);
    const [zipcode, setZipcode] = useAtom<string>(zipCodeState);
    const [incentives, setIncentives] = useAtom<IncentiveApiData[]>(incentivesData);
    const [residentialIncentives, setResidentialIncentives] =
        useAtom<ResidentialIncentiveApiData[]>(residentialIncentivesData);
    const [isZipcodeValid, setIsZipcodeValid] = useState(false);
    const [zipcodeError, setZipcodeError] = useState('');
    const [zipcodeErrorMessage, setZipcodeErrorMessage] = useState('');
    const [filteredIncentives] = useAtom<IncentiveApiData[]>(filteredIncentivesData);
    const [filteredResidentialIncentives] = useAtom<ResidentialIncentiveApiData[]>(
        filteredResidentialIncentivesData,
    );
    const [selectedStatus, setSelectedStatus] = useAtom<string>(status);
    const [selectedOrgType, setSelectedOrgType] = useAtom<string>(organizationType);
    const [selectedEquipment, setSelectedEquipment] = useAtom<string>(equipmentType);
    const [selectedWeightClass, setSelectedWeightClass] = useAtom<WeightClass[]>(weightClassData);
    const [selectedChargerType, setSelectedChargerType] = useAtom<string>(chargerType);
    const [selectedIncentives, setSelectedIncentives] = useAtom(selectedIncentivesData);

    const [errorBanner, setErrorBanner] = useState<boolean>(false);
    const [errorBannerDescription, setErrorBannerDescription] = useState<string>('');
    const [errorBannerMessage, setErrorBannerMessage] = useState<string>('');
    const [
        selectedResIncentiveApplicableVehicleType,
        setSelectedResIncentiveApplicableVehicleType,
    ] = useAtom<string>(residentialIncentiveApplicableVehicleType);
    const [selectedResIncentiveElectricityProvider, setSelectedResIncentiveElectricityProvider] =
        useAtom<string>(residentialIncentiveElectricityProvider);
    const [selectedResIncentiveGrantorType, setSelectedResIncentiveGrantorType] = useAtom<string>(
        residentialIncentiveGrantorType,
    );
    const [selectedResidentialEligibleEquipment, setSelectedResidentialEligibleEquipment] =
        useAtom<string>(residentialEquipmentType);
    const handleViewIncentivesClick = useCallback(async () => {
        if (isZipcodeValid) {
            try {
                setIsLoading(true);
                let response;

                if (selectedIncentiveType === IncentiveTypes.COMMERCIAL) {
                    response = await getCommercialIncentives(zipcode);
                    setIncentives(response?.data || []);
                } else if (selectedIncentiveType === IncentiveTypes.RESIDENTIAL) {
                    response = await getResidentialIncentives(zipcode);
                    setResidentialIncentives(response?.data || []);
                }

                setErrorBanner(false);
            } catch (error) {
                const axiosError = error as AxiosError;
                if (selectedIncentiveType === IncentiveTypes.COMMERCIAL) setIncentives([]);
                else setResidentialIncentives([]);

                setErrorBanner(true);
                const status = axiosError?.response?.status || 500;
                const errorInfo = errorMapping[status];
                setErrorBannerMessage(errorInfo.message);
                setErrorBannerDescription(errorInfo.description);
            } finally {
                setIsLoading(false);
            }
        }
    }, [
        isZipcodeValid,
        setIncentives,
        setResidentialIncentives,
        setErrorBanner,
        setErrorBannerDescription,
        setErrorBannerMessage,
        setIsLoading,
        selectedIncentiveType,
        zipcode,
    ]);

    const incentivesToDisplay =
        selectedIncentiveType === IncentiveTypes.COMMERCIAL ? incentives : residentialIncentives;

    const [selectedResidentialIncentives, setSelectedResidentialIncentives] = useAtom(
        selectedResidentialIncentiveData,
    );
    const getExportButtonLabel = () => {
        if (selectedIncentiveType === IncentiveTypes.COMMERCIAL) {
            return !selectedIncentives.length ? 'Export (.pdf)' : 'Export Selected (PDF)';
        } else if (selectedIncentiveType === IncentiveTypes.RESIDENTIAL) {
            return !selectedResidentialIncentives.length
                ? 'Export (.pdf)'
                : 'Export Selected (PDF)';
        }
        return 'Export (.pdf)';
    };

    useEffect(() => {
        const validateZipcodeAndFetch = () => {
            if (!!zipcode && !isZipcodeValid) {
                validateZipcode(zipcode);
            }
        };
        validateZipcodeAndFetch();
        const enterKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleViewIncentivesClick();
            }
        };
        window.addEventListener('keydown', enterKeyDown);
        return () => {
            window.removeEventListener('keydown', enterKeyDown);
        };
    }, [isZipcodeValid, zipcode, handleViewIncentivesClick]);

    const getExportRoute = (incentiveType: string) => {
        switch (incentiveType) {
            case IncentiveTypes.COMMERCIAL:
                return `${FPG_PAGE_ROUTE}${EXPORT_PDF_PAGE_COMMERCIAL_ROUTE}`;
            case IncentiveTypes.RESIDENTIAL:
                return `${FPG_PAGE_ROUTE}${EXPORT_PDF_PAGE_RESIDENTIAL_ROUTE}`;
            default:
                return null;
        }
    };

    const handleExport = () => {
        navigate(getExportRoute(selectedIncentiveType));
    };

    const isFilterApplied = [
        selectedStatus,
        selectedOrgType,
        selectedEquipment,
        selectedResIncentiveApplicableVehicleType,
        selectedResIncentiveElectricityProvider,
        selectedResIncentiveGrantorType,
        selectedResidentialEligibleEquipment,
    ].some((val) => val !== DropdownConstants.ALL);

    const isExportButtonDisabled = () => {
        if (isLoading) return true;

        return (
            (isFilterApplied ? filteredIncentives.length === 0 : incentives.length === 0) &&
            (isFilterApplied
                ? filteredResidentialIncentives.length === 0
                : residentialIncentives.length === 0)
        );
    };

    const handleMobileDropdownChange = (value: any) => {
        const button = JSON.parse(value.target.value).label;
        if (button === 'Export') {
            handleExport();
        }
    };

    const handleZipcodeChange = (value: string) => {
        if (value.length > 5) {
            value = value.substring(0, value.length - 1);
        }
        setZipcode(value);
        validateZipcode(value);
    };

    const handleIncentiveTypeChange = (value: string) => {
        setSelectedIncentiveType(value);
        if (value === IncentiveTypes.COMMERCIAL) {
            setSelectedResidentialIncentives([]);
        } else if (value === IncentiveTypes.RESIDENTIAL) {
            setSelectedIncentives([]);
        } else if (value === '') {
            setIncentives([]);
        }
    };

    const handleEquipmentChange = (value: string) => {
        if (value === EQUIPMENT_TYPE_CHARGER) {
            setSelectedWeightClass([]);
        }
        if (value === EQUIPMENT_TYPE_VEHICLE) {
            setSelectedChargerType(DropdownConstants.ALL);
        }
        setSelectedEquipment(value);
    };

    const validateZipcode = (value: string) => {
        const zipcodeRegex = /^\d{5}$/;
        setIsZipcodeValid(zipcodeRegex.test(value));
        if (!zipcodeRegex.test(value)) {
            setZipcodeError('error');
            if (value == '') {
                setZipcodeErrorMessage('Field cannot be empty');
            } else if (value.length < 5) {
                setZipcodeErrorMessage('Enter at least 5 characters');
            } else {
                setZipcodeErrorMessage('Enter a valid ZIP code');
            }
        } else {
            setZipcodeError('');
            setZipcodeErrorMessage('');
        }
    };

    const renderIncentiveFilters = () => {
        if (selectedIncentiveType === IncentiveTypes.COMMERCIAL) {
            return (
                <Grid>
                    <Grid.Item xl={2} l={2} m={3} s={3}>
                        <Dropdown
                            label={'Status'}
                            // @ts-expect-error - This is a known issue with the FPDS component library
                            tooltip={React.createElement(StatusToolTip)}
                            options={getUniqueCurrentProgramStatuses(incentives)}
                            dataTestId="status-dropdown"
                            onChange={setSelectedStatus}
                            selected={selectedStatus}
                        />
                    </Grid.Item>

                    <Grid.Item xl={2} l={3} m={4} s={4}>
                        <Dropdown
                            label={'Organization Type'}
                            // @ts-expect-error - This is a known issue with the FPDS component library
                            tooltip={React.createElement(PlainTextToolTip, {
                                tooltipContent: ORG_TYPE_TOOLTIP,
                            })}
                            options={getUniqueEligibleOrganizations(incentives)}
                            dataTestId="org-type-dropdown"
                            onChange={setSelectedOrgType}
                            selected={selectedOrgType}
                        />
                    </Grid.Item>
                    <Grid.Item xl={2} l={2} m={3} s={3}>
                        <Dropdown
                            label={'Equipment'}
                            tooltip={''}
                            options={getUniqueEligibleEquipments(incentives)}
                            dataTestId="equipment-dropdown"
                            onChange={handleEquipmentChange}
                            selected={selectedEquipment}
                        />
                    </Grid.Item>
                    {selectedEquipment === 'vehicles' && (
                        <Grid.Item
                            className={`${styles.weightClassDropdownWrapper} ${
                                isEmpty(selectedWeightClass) ? styles.empty : styles.filled
                            }`}
                            xl={6}
                            l={6}
                            m={3}
                            s={3}
                        >
                            <WeightClassDropdown />
                        </Grid.Item>
                    )}
                    {selectedEquipment === EQUIPMENT_TYPE_CHARGER && (
                        <Grid.Item xl={2} l={2} m={3} s={3}>
                            <Dropdown
                                label={'Charger Type'}
                                tooltip={''}
                                options={getUniqueEligibleChargingTechnology(incentives)}
                                dataTestId="chargerType-dropdown"
                                onChange={setSelectedChargerType}
                                selected={selectedChargerType}
                            />
                        </Grid.Item>
                    )}
                </Grid>
            );
        } else if (selectedIncentiveType === IncentiveTypes.RESIDENTIAL) {
            return (
                <ResidentialInputFieldsContainer
                    selectedResIncentiveGrantorType={selectedResIncentiveGrantorType}
                    setSelectedResIncentiveGrantorType={setSelectedResIncentiveGrantorType}
                    selectedResIncentiveElectricityProvider={
                        selectedResIncentiveElectricityProvider
                    }
                    setSelectedResIncentiveElectricityProvider={
                        setSelectedResIncentiveElectricityProvider
                    }
                    selectedResIncentiveApplicableVehicleType={
                        selectedResIncentiveApplicableVehicleType
                    }
                    setSelectedResIncentiveApplicableVehicleType={
                        setSelectedResIncentiveApplicableVehicleType
                    }
                    selectedResidentialEligibleEquipment={selectedResidentialEligibleEquipment}
                    setSelectedResidentialEligibleEquipment={
                        setSelectedResidentialEligibleEquipment
                    }
                />
            );
        } else {
            return null;
        }
    };

    return (
        <div className={styles.container}>
            {errorBanner && (
                <ErrorDisplay
                    body={errorBannerDescription}
                    title={errorBannerMessage}
                    setErrorBanner={setErrorBanner}
                />
            )}
            <Grid>
                <Grid.Item xl={2} l={2} m={4} s={12}>
                    <Dropdown
                        label={'Incentive Type'}
                        tooltip={''}
                        isRequired={true}
                        showClear={true}
                        placeholder="Select"
                        options={[
                            {
                                label: IncentiveTypes.COMMERCIAL,
                                value: IncentiveTypes.COMMERCIAL,
                            },
                            {
                                label: IncentiveTypes.RESIDENTIAL,
                                value: IncentiveTypes.RESIDENTIAL,
                            },
                        ]}
                        dataTestId="incentive-type-dropdown"
                        onChange={handleIncentiveTypeChange}
                        selected={selectedIncentiveType}
                    />
                </Grid.Item>
                {selectedIncentiveType !== '' && (
                    <>
                        <Grid.Item xl={2} l={2} m={4} s={12}>
                            <SearchWithToolTip
                                label={'Zip Code'}
                                // @ts-expect-error - This is a known issue with the FPDS component library
                                tooltip={React.createElement(PlainTextToolTip, {
                                    tooltipContent: ZIPCODE_TOOLTIP,
                                })}
                                required={true}
                                onChange={handleZipcodeChange}
                                value={zipcode}
                                error={zipcodeError ? 'error' : ''}
                                errorText={zipcodeErrorMessage}
                                placeholder="e.g. 12345"
                            />
                        </Grid.Item>
                        <Grid.Item
                            style={{ paddingTop: '30px', width: '170px' }}
                            xl={2}
                            l={2}
                            m={4}
                            s={12}
                        >
                            <ZappyButton
                                label={'View Incentives'}
                                buttonType="default"
                                onClick={handleViewIncentivesClick}
                                disabled={!isZipcodeValid}
                                dataTestId="view-incentive-btn"
                            />
                        </Grid.Item>
                        <Grid.Item
                            style={{
                                display: 'flex',
                                paddingTop: '30px',
                                marginLeft: 'auto',
                            }}
                            xl={3}
                            l={3}
                            m={1}
                            s={1}
                        >
                            {isDesktop(width) ? (
                                <Grid>
                                    <Grid.Item
                                        className={styles.actionWrapper}
                                        style={{ display: 'flex' }}
                                        xl={12}
                                        l={12}
                                        m={12}
                                        s={12}
                                    >
                                        {!isExportButtonDisabled() && (
                                            <div className={styles.exportBtn}>
                                                <ZappyButton
                                                    label={getExportButtonLabel()}
                                                    buttonType="secondary"
                                                    disabled={isExportButtonDisabled()}
                                                    dataTestId="export-btn"
                                                    onClick={handleExport}
                                                />
                                            </div>
                                        )}
                                    </Grid.Item>
                                </Grid>
                            ) : (
                                <div style={{ marginLeft: 'auto' }}>
                                    <DropdownMenu
                                        onChange={handleMobileDropdownChange}
                                        menuDirection="down_right"
                                    >
                                        <DropdownMenu.Item
                                            label="Export"
                                            value="Export"
                                            disabled={isExportButtonDisabled()}
                                        />
                                    </DropdownMenu>
                                </div>
                            )}
                        </Grid.Item>
                    </>
                )}
            </Grid>
            {incentivesToDisplay &&
                (incentivesToDisplay.length > 0 || isLoading) &&
                renderIncentiveFilters()}
        </div>
    );
};

export default InputFieldsContainer;
